import { produce } from 'immer'
import {
  RESET_CSP_SIGNIN_STATE,
  ResetCspSigninStateAction,
  SET_SELECTED_CSP_SESSION_TYPE,
  SetSelectedCspSessionTypeAction,
} from 'src/app/CspSignin/redux/cspSignin.actions'
import { cspSigninRouteProps } from 'src/navigation/routeProps'
import {
  CONFIRM_COGNITO_SIGNIN_FAILURE,
  CONFIRM_COGNITO_SIGNIN_INIT,
  CONFIRM_COGNITO_SIGNIN_SUCCESS,
  ConfirmCognitoSigninActions,
  POST_CSP_EMULATED_SESSIONS_LIVE_FAILURE,
  POST_CSP_EMULATED_SESSIONS_LIVE_INIT,
  POST_CSP_EMULATED_SESSIONS_LIVE_SUCCESS,
  POST_CSP_EMULATED_SESSIONS_OFFLINE_FAILURE,
  POST_CSP_EMULATED_SESSIONS_OFFLINE_INIT,
  POST_CSP_EMULATED_SESSIONS_OFFLINE_SUCCESS,
  POST_CSP_LOGIN_FAILURE,
  POST_CSP_LOGIN_INIT,
  POST_CSP_LOGIN_SUCCESS,
  POST_LOOKUP_FAILURE,
  POST_LOOKUP_INIT,
  POST_LOOKUP_SUCCESS,
  POST_USER_RESEND_VERIFICATION_FAILURE,
  POST_USER_RESEND_VERIFICATION_INIT,
  POST_USER_RESEND_VERIFICATION_SUCCESS,
  POST_USER_VERIFICATION_FAILURE,
  POST_USER_VERIFICATION_INIT,
  POST_USER_VERIFICATION_SUCCESS,
  PostCspEmulatedSessionsLiveActions,
  PostCspEmulatedSessionsOfflineActions,
  PostCspLoginActions,
  PostLookupActions,
  PostUserResendVerificationActions,
  PostUserVerificationActions,
  PUT_CSP_EMULATED_SESSIONS_RESEND_FAILURE,
  PUT_CSP_EMULATED_SESSIONS_RESEND_INIT,
  PUT_CSP_EMULATED_SESSIONS_RESEND_SUCCESS,
  PUT_CSP_EMULATED_SESSIONS_VERIFY_FAILURE,
  PUT_CSP_EMULATED_SESSIONS_VERIFY_INIT,
  PUT_CSP_EMULATED_SESSIONS_VERIFY_SUCCESS,
  PutCspEmulatedSessionsResendActions,
  PutCspEmulatedSessionsVerifyActions,
} from 'src/redux/actions/auth.actions'
import { createBackgroundReducer } from 'src/redux/genericReducers'
import { ErrorDetailMap, parseBackendErrors } from 'src/utils/error.utils'

export enum CspSessionType {
  Live,
  Offline,
}
export interface CspSigninState {
  isLookingUp: boolean
  isCspLoggingIn: boolean
  isVerifyingUser: boolean
  isPostingCspEmulatedSessionsOffline: boolean
  isPostingCspEmulatedSessionsLive: boolean
  isPuttingCspEmulatedSessionsVerify: boolean
  lookupTopLevelError?: string
  lookupErrorDetailMap?: ErrorDetailMap<string>
  cspLoginTopLevelError?: string
  cspLoginErrorDetailMap?: ErrorDetailMap<string>
  postCspEmulatedSessionsOfflineTopLevelError?: string
  postCspEmulatedSessionsOfflineErrorDetailMap?: ErrorDetailMap<string>
  postCspEmulatedSessionsLiveTopLevelError?: string
  postCspEmulatedSessionsLiveErrorDetailMap?: ErrorDetailMap<string>
  putCspEmulatedSessionsVerifyTopLevelError?: string
  putCspEmulatedSessionsVerifyErrorDetailMap?: ErrorDetailMap<string>
  verificationEmailResent: boolean
  resendVerificationTopLevelError?: string
  verifyEmailTopLevelError?: string
  hasSentToken: boolean
  selectedCspSessionType?: CspSessionType
  userSessionTokenId?: string // NOTE: this is distinct from the userSessionTokenId in AUTH state, which is persistent
  userId?: string // NOTE: this is distinct from the userId in AUTH state, which is persistent
}

export const initialState: CspSigninState = {
  isLookingUp: false,
  isCspLoggingIn: false,
  isVerifyingUser: false,
  isPostingCspEmulatedSessionsOffline: false,
  isPostingCspEmulatedSessionsLive: false,
  isPuttingCspEmulatedSessionsVerify: false,
  verificationEmailResent: false,
  hasSentToken: false,
}

export type Actions =
  | ResetCspSigninStateAction
  | PostLookupActions
  | PostCspLoginActions
  | PostUserVerificationActions
  | PostUserResendVerificationActions
  | SetSelectedCspSessionTypeAction
  | PostCspEmulatedSessionsOfflineActions
  | PostCspEmulatedSessionsLiveActions
  | PutCspEmulatedSessionsResendActions
  | PutCspEmulatedSessionsVerifyActions
  | ConfirmCognitoSigninActions

const CoreCspSigninReducer = (
  state = initialState,
  action: Actions,
): CspSigninState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case RESET_CSP_SIGNIN_STATE: {
        return initialState
      }

      case PUT_CSP_EMULATED_SESSIONS_RESEND_INIT: {
        draft.hasSentToken = false
        return draft
      }

      case PUT_CSP_EMULATED_SESSIONS_RESEND_SUCCESS: {
        draft.hasSentToken = true
        return draft
      }

      case PUT_CSP_EMULATED_SESSIONS_RESEND_FAILURE: {
        draft.hasSentToken = false
        return draft
      }

      case SET_SELECTED_CSP_SESSION_TYPE: {
        draft.selectedCspSessionType = action.payload.sessionType
        draft.cspLoginTopLevelError = undefined
        draft.cspLoginErrorDetailMap = undefined
        draft.postCspEmulatedSessionsOfflineTopLevelError = undefined
        draft.postCspEmulatedSessionsOfflineErrorDetailMap = undefined
        return draft
      }
      case POST_LOOKUP_INIT: {
        draft.isLookingUp = true
        draft.lookupTopLevelError = undefined
        draft.lookupErrorDetailMap = undefined
        draft.verifyEmailTopLevelError = undefined
        draft.resendVerificationTopLevelError = undefined
        draft.cspLoginTopLevelError = undefined
        draft.cspLoginErrorDetailMap = undefined
        return draft
      }

      case POST_LOOKUP_SUCCESS: {
        draft.isLookingUp = false
        return draft
      }

      case POST_LOOKUP_FAILURE: {
        draft.isLookingUp = false
        const errors = parseBackendErrors(action.payload)
        draft.lookupTopLevelError = errors.topLevelError
        draft.lookupErrorDetailMap = errors.errorDetailMap
        return draft
      }

      case POST_CSP_LOGIN_INIT: {
        draft.isCspLoggingIn = true
        draft.cspLoginTopLevelError = undefined
        draft.cspLoginErrorDetailMap = undefined
        return draft
      }

      case POST_CSP_LOGIN_SUCCESS: {
        draft.isCspLoggingIn = false
        return draft
      }

      case POST_CSP_LOGIN_FAILURE: {
        draft.isCspLoggingIn = false
        const errors = parseBackendErrors(action.payload)
        draft.cspLoginTopLevelError = errors.topLevelError
        draft.cspLoginErrorDetailMap = errors.errorDetailMap
        return draft
      }

      case POST_CSP_EMULATED_SESSIONS_OFFLINE_INIT: {
        draft.isPostingCspEmulatedSessionsOffline = true
        draft.postCspEmulatedSessionsOfflineTopLevelError = undefined
        draft.postCspEmulatedSessionsOfflineErrorDetailMap = undefined
        return draft
      }

      case POST_CSP_EMULATED_SESSIONS_OFFLINE_SUCCESS: {
        draft.isPostingCspEmulatedSessionsOffline = false
        return draft
      }

      case POST_CSP_EMULATED_SESSIONS_OFFLINE_FAILURE: {
        draft.isPostingCspEmulatedSessionsOffline = false
        const errors = parseBackendErrors(action.payload)
        draft.postCspEmulatedSessionsOfflineTopLevelError = errors.topLevelError
        draft.postCspEmulatedSessionsOfflineErrorDetailMap =
          errors.errorDetailMap
        return draft
      }

      case POST_CSP_EMULATED_SESSIONS_LIVE_INIT: {
        draft.isPostingCspEmulatedSessionsLive = true
        draft.postCspEmulatedSessionsLiveTopLevelError = undefined
        draft.postCspEmulatedSessionsLiveErrorDetailMap = undefined
        return draft
      }

      case POST_CSP_EMULATED_SESSIONS_LIVE_SUCCESS: {
        draft.isPostingCspEmulatedSessionsLive = false
        draft.userId = action.payload.token.resource.emulatedUser.id
        draft.userSessionTokenId = action.payload.token.id
        return draft
      }

      case POST_CSP_EMULATED_SESSIONS_LIVE_FAILURE: {
        draft.isPostingCspEmulatedSessionsLive = false
        const errors = parseBackendErrors(action.payload)
        draft.postCspEmulatedSessionsLiveTopLevelError = errors.topLevelError
        draft.postCspEmulatedSessionsLiveErrorDetailMap = errors.errorDetailMap
        return draft
      }

      case PUT_CSP_EMULATED_SESSIONS_VERIFY_INIT: {
        draft.isPuttingCspEmulatedSessionsVerify = true
        draft.putCspEmulatedSessionsVerifyTopLevelError = undefined
        draft.putCspEmulatedSessionsVerifyErrorDetailMap = undefined
        return draft
      }

      case PUT_CSP_EMULATED_SESSIONS_VERIFY_SUCCESS: {
        draft.isPuttingCspEmulatedSessionsVerify = false
        return draft
      }

      case PUT_CSP_EMULATED_SESSIONS_VERIFY_FAILURE: {
        draft.isPuttingCspEmulatedSessionsVerify = false
        const errors = parseBackendErrors(action.payload)
        draft.putCspEmulatedSessionsVerifyTopLevelError = errors.topLevelError
        draft.putCspEmulatedSessionsVerifyErrorDetailMap = errors.errorDetailMap
        return draft
      }

      case POST_USER_VERIFICATION_INIT: {
        draft.isVerifyingUser = true
        draft.verifyEmailTopLevelError = undefined
        return draft
      }

      case POST_USER_VERIFICATION_SUCCESS: {
        draft.isVerifyingUser = false
        return draft
      }

      case POST_USER_VERIFICATION_FAILURE: {
        draft.isVerifyingUser = false
        const errors = parseBackendErrors(action.payload)
        draft.verifyEmailTopLevelError = errors.topLevelError
        return draft
      }

      case POST_USER_RESEND_VERIFICATION_INIT: {
        draft.verificationEmailResent = false
        draft.resendVerificationTopLevelError = undefined
        return draft
      }

      case POST_USER_RESEND_VERIFICATION_SUCCESS: {
        draft.verificationEmailResent = true
        return draft
      }

      case POST_USER_RESEND_VERIFICATION_FAILURE: {
        const errors = parseBackendErrors(action.payload)
        draft.resendVerificationTopLevelError = errors.topLevelError
        return draft
      }

      case CONFIRM_COGNITO_SIGNIN_INIT: {
        draft.isVerifyingUser = true
        draft.verifyEmailTopLevelError = undefined
        return draft
      }

      case CONFIRM_COGNITO_SIGNIN_SUCCESS: {
        draft.isVerifyingUser = false
        return draft
      }

      case CONFIRM_COGNITO_SIGNIN_FAILURE: {
        draft.isVerifyingUser = false
        draft.verifyEmailTopLevelError = action.payload
        return draft
      }
    }
  })
}

const CspSigninReducer = createBackgroundReducer(
  cspSigninRouteProps,
  initialState,
  CoreCspSigninReducer,
)

export default CspSigninReducer
